/**
 * Session identity + attribution: first touch, persisted.
 *
 * The first landing writes one first-party cookie (`bsy_attr`, 90 days,
 * SameSite=Lax) holding the session id and every attribution param, mirrored
 * in sessionStorage. Later visits never overwrite it — first touch wins — so
 * the ad that brought the visitor is the ad the purchase is credited to, even
 * a week later. The Meta cookies (`_fbp`, `_fbc`) are read alongside; `_fbc`
 * is constructed from `fbclid` when the pixel has not set it yet.
 */

export interface Attribution {
  utm_source?: string; utm_medium?: string; utm_campaign?: string; utm_content?: string; utm_term?: string;
  fbclid?: string; gclid?: string; ad_id?: string; adset_id?: string; campaign_id?: string; placement?: string;
  referrer?: string;
  landing_url?: string;
  angle?: string;
}

export const ATTRIBUTION_KEYS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
  'fbclid', 'gclid', 'ad_id', 'adset_id', 'campaign_id', 'placement',
] as const;

export interface FirstTouch extends Attribution {
  sid: string;
  ts: number;
  lang?: string;
  /** the funnel JSON that rendered */
  funnel_code?: string;
  /** the `f` param of the landing: the ad angle (A1-views, A2-decibel …), which need not be a funnel */
  angle?: string;
}

export const ATTR_COOKIE = 'bsy_attr';
const COOKIE_DAYS = 90;

const SID_KEY = 'bf:sid';
const ATTR_KEY = 'bf:attribution';
const LEAD_KEY = 'bf:lead_id';
const EMAIL_KEY = 'bf:lead_email';

export const uuid = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      });

function readStore(key: string): string | null {
  try { return sessionStorage.getItem(key); } catch { return null; }
}
function writeStore(key: string, value: string): void {
  try { sessionStorage.setItem(key, value); } catch { /* private mode */ }
}

/* ------------------------------------------------------------------ cookies */

export function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}

export function writeCookie(name: string, value: string, days = COOKIE_DAYS): void {
  if (typeof document === 'undefined') return;
  const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
  try {
    document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${days * 86400}; Path=/; SameSite=Lax${secure}`;
  } catch {
    /* cookies blocked */
  }
}

/* -------------------------------------------------------------- first touch */

let cached: FirstTouch | null = null;

function parseFirstTouch(raw: string | null): FirstTouch | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as FirstTouch;
    return v && typeof v.sid === 'string' ? v : null;
  } catch {
    return null;
  }
}

/**
 * The first touch for this browser: read from the cookie (or its sessionStorage
 * mirror), or captured now from the URL and written once. URLSearchParams
 * already percent-decodes, so an ad name like `Boostsy | EN | S1 | H01` comes
 * back with its pipes and spaces intact — decoding it a second time would
 * break any value containing a literal `%`.
 */
export function firstTouch(search: string = typeof location !== 'undefined' ? location.search : '', route?: { funnel_code?: string; lang?: string }): FirstTouch {
  const existing = cached ?? parseFirstTouch(readCookie(ATTR_COOKIE)) ?? parseFirstTouch(readStore(ATTR_KEY));
  if (existing) {
    // the funnel and language are only known once the config has loaded, so
    // they are filled in on the touch that was written before that — never
    // replaced: the first funnel this browser landed on stays
    let changed = false;
    if (route?.funnel_code && !existing.funnel_code) {
      existing.funnel_code = route.funnel_code;
      changed = true;
    }
    if (route?.lang && !existing.lang) {
      existing.lang = route.lang;
      changed = true;
    }
    const json = JSON.stringify(existing);
    if (changed || cached !== existing) {
      if (changed) writeCookie(ATTR_COOKIE, json);
      // keep the mirror and the tab-scoped sid in step with the cookie
      writeStore(ATTR_KEY, json);
      writeStore(SID_KEY, existing.sid);
    }
    cached = existing;
    return existing;
  }

  const params = new URLSearchParams(search);
  const touch: FirstTouch = { sid: readStore(SID_KEY) ?? uuid(), ts: Date.now() };
  for (const k of ATTRIBUTION_KEYS) {
    const v = params.get(k);
    if (v) touch[k] = v;
  }
  const angle = params.get('f');
  if (angle) touch.angle = angle;
  if (route?.funnel_code) touch.funnel_code = route.funnel_code;
  if (route?.lang) touch.lang = route.lang;
  if (typeof document !== 'undefined' && document.referrer) touch.referrer = document.referrer;
  if (typeof location !== 'undefined') touch.landing_url = location.href;

  const json = JSON.stringify(touch);
  writeCookie(ATTR_COOKIE, json);
  writeStore(ATTR_KEY, json);
  writeStore(SID_KEY, touch.sid);
  cached = touch;
  return touch;
}

/** test hook: forget the in-memory first touch (the cookie itself is left alone) */
export const resetFirstTouchCache = (): void => {
  cached = null;
};

export function sessionId(): string {
  return firstTouch().sid;
}

export function captureAttribution(search?: string): Attribution {
  const touch = firstTouch(search);
  const attr: Attribution = {};
  for (const k of ATTRIBUTION_KEYS) if (touch[k]) attr[k] = touch[k];
  if (touch.referrer) attr.referrer = touch.referrer;
  if (touch.landing_url) attr.landing_url = touch.landing_url;
  if (touch.angle) attr.angle = touch.angle;
  return attr;
}

/* ------------------------------------------------------------- meta cookies */

/** `fb.1.<unix ms>.<fbclid>` — the format the pixel itself writes */
export const fbcFrom = (fbclid: string | undefined, ts: number): string | undefined => (fbclid ? `fb.1.${ts}.${fbclid}` : undefined);

/**
 * `_fbp` and `_fbc` as the pixel set them; when `_fbc` is missing but the
 * landing carried an `fbclid`, build it and set the cookie so the pixel and
 * the server agree on the click.
 */
export function metaCookies(): { fbp?: string; fbc?: string } {
  const fbp = readCookie('_fbp') ?? undefined;
  let fbc = readCookie('_fbc') ?? undefined;
  if (!fbc) {
    const touch = firstTouch();
    fbc = fbcFrom(touch.fbclid, touch.ts);
    if (fbc) writeCookie('_fbc', fbc);
  }
  return { fbp, fbc };
}

/* ------------------------------------------------------------ once-per-session */

const ONCE_PREFIX = 'bf:once:';

/** true the first time a name is seen in this session, false after — reloads and back-steps do not re-fire */
export function once(name: string): boolean {
  const key = `${ONCE_PREFIX}${name}`;
  if (readStore(key)) return false;
  writeStore(key, '1');
  return true;
}

export const leadId = (): string | null => readStore(LEAD_KEY);
export const setLeadId = (id: string): void => writeStore(LEAD_KEY, id);

/** the address the lead was created with; Paddle needs it to open a checkout */
export const leadEmail = (): string | null => readStore(EMAIL_KEY);
export const setLeadEmail = (email: string): void => writeStore(EMAIL_KEY, email);

export function device(): 'ios' | 'android' | 'desktop' {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

export const inAppBrowser = (): boolean =>
  /Instagram|FBAN|FBAV|FB_IAB/i.test(typeof navigator !== 'undefined' ? navigator.userAgent : '');

export async function sha256(input: string): Promise<string> {
  const data = new TextEncoder().encode(input.trim().toLowerCase());
  const digest = await crypto.subtle.digest('SHA-256', data);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
